import React, { useMemo, useRef, useState } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, RefreshCw, TrendingUp, Award, Target, Download, Loader2 } from "lucide-react";
import { RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { toast } from "sonner";
import { getModule, tierForScore, computeScore } from "../data/assessments";
import { useAssessments } from "../context/AssessmentContext";
import { exportAssessmentPDF } from "../lib/pdfExport";

const AssessmentResults = () => {
  const { slug } = useParams();
  const mod = useMemo(() => getModule(slug), [slug]);
  const { results, clearResult } = useAssessments();
  const stored = results[slug];

  const answers = stored?.answers || {};
  const dims = mod?.dimensions || [];
  const score = stored ? (stored.score || computeScore(answers, dims)) : { pct: 0, sum: 0, max: 0 };
  const tier = tierForScore(score.pct);

  const radarData = dims.map(d => ({
    dimension: d.label.length > 14 ? d.label.split(" ")[0] : d.label,
    score: answers[d.key] || 0,
    benchmark: 3.5,
  }));

  const barData = dims.map(d => ({
    name: d.label,
    score: answers[d.key] || 0,
  })).sort((a, b) => b.score - a.score);

  const insights = useMemo(() => {
    const strengths = barData.filter(b => b.score >= 4).slice(0, 3);
    const gaps = barData.filter(b => b.score <= 2).slice(0, 3);
    return { strengths, gaps };
  }, [barData]);

  const chartsRef = useRef(null);
  const [exporting, setExporting] = useState(false);

  if (!mod) return <Navigate to="/assessment" replace />;
  if (!stored) return <Navigate to={`/assessment/${slug}`} replace />;

  const retake = () => {
    clearResult(slug);
  };

  const downloadPDF = async () => {
    if (exporting) return;
    setExporting(true);
    try {
      await exportAssessmentPDF({ mod, answers, score, tier, chartsEl: chartsRef.current });
      toast.success("Report downloaded.");
    } catch (e) {
      toast.error("Could not generate PDF. Please try again.");
    } finally {
      setExporting(false);
    }
  };

  return (
    <div data-testid="assessment-results-page" className="min-h-screen pt-28 pb-20 bg-gradient-to-b from-brand-green-50/30 to-white">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        {/* Header */}
        <div className="flex items-center justify-between mb-10 gap-4 flex-wrap">
          <Link to="/assessment" data-testid="results-back" className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-green transition-colors">
            <ArrowLeft className="h-4 w-4" /> Back to assessments
          </Link>
          <div className="flex items-center gap-5">
            <Link to={`/assessment/${slug}`} onClick={retake} data-testid="results-retake" className="inline-flex items-center gap-2 text-sm text-brand-slate hover:text-brand-green transition-colors">
              <RefreshCw className="h-4 w-4" /> Retake
            </Link>
            <button
              onClick={downloadPDF}
              disabled={exporting}
              data-testid="results-download-pdf"
              className="inline-flex items-center gap-2 bg-brand-green text-white px-5 py-2.5 rounded-sm text-sm font-medium hover:bg-brand-green-light disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
            >
              {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
              {exporting ? "Generating…" : "Download PDF Report"}
            </button>
          </div>
        </div>

        <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-3">Executive Summary</div>
        <h1 className="font-serif text-4xl md:text-6xl text-brand-ink tracking-tight leading-[1.05]">{mod.name}<span className="text-brand-slate"> · </span><em className="text-brand-green font-serif italic">Results</em></h1>
        <p className="mt-5 text-brand-slate max-w-2xl">{mod.subhead}</p>

        {/* KPI Cards */}
        <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div data-testid="kpi-score" className="bg-brand-ink text-white rounded-sm p-8 relative overflow-hidden">
            <div className="absolute -right-12 -top-12 h-40 w-40 rounded-full bg-brand-green/40 blur-2xl" />
            <div className="relative">
              <div className="text-xs tracking-[0.25em] uppercase text-brand-gold">Overall Score</div>
              <div className="font-serif text-7xl mt-3 tracking-tight">{score.pct}<span className="text-3xl text-white/40">%</span></div>
              <div className="mt-2 text-white/60 text-sm">{score.sum} / {score.max} points</div>
            </div>
          </div>
          <div className="bg-white border border-black/5 rounded-sm p-8 shadow-glass">
            <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Maturity Tier</div>
            <div className="font-serif text-4xl mt-3 tracking-tight" style={{ color: tier.color }}>{tier.tier}</div>
            <div className="mt-3 text-brand-slate text-sm leading-relaxed">{tier.desc}</div>
          </div>
          <div className="bg-white border border-black/5 rounded-sm p-8 shadow-glass">
            <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Industry Benchmark</div>
            <div className="font-serif text-4xl mt-3 tracking-tight text-brand-ink">{score.pct - 8 > 0 ? "+" : ""}{score.pct - 70}%</div>
            <div className="mt-3 text-brand-slate text-sm leading-relaxed">vs. industry median (70%) for {mod.name.toLowerCase()}.</div>
          </div>
        </div>

        {/* Radar + Bar */}
        <div ref={chartsRef} className="mt-12 grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-7 bg-white border border-black/5 rounded-sm p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Dimension Spread</div>
                <h3 className="font-serif text-2xl text-brand-ink mt-1">Capability radar</h3>
              </div>
              <Target className="h-5 w-5 text-brand-gold" />
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <RadarChart data={radarData} outerRadius="75%">
                  <PolarGrid stroke="#E2E8F0" />
                  <PolarAngleAxis dataKey="dimension" tick={{ fill: "#64748B", fontSize: 11 }} />
                  <PolarRadiusAxis angle={90} domain={[0, 5]} tick={{ fill: "#94A3B8", fontSize: 10 }} />
                  <Radar name="Benchmark" dataKey="benchmark" stroke="#C5A265" fill="#C5A265" fillOpacity={0.12} strokeDasharray="4 4" />
                  <Radar name="You" dataKey="score" stroke="#0F4C3A" fill="#0F4C3A" fillOpacity={0.45} strokeWidth={2} />
                  <Tooltip contentStyle={{ background: "#1A1C19", border: "none", borderRadius: 2, color: "white", fontSize: 12 }} />
                </RadarChart>
              </ResponsiveContainer>
            </div>
          </div>
          <div className="lg:col-span-5 bg-white border border-black/5 rounded-sm p-8">
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="text-xs tracking-[0.25em] uppercase text-brand-slate">Ranked Performance</div>
                <h3 className="font-serif text-2xl text-brand-ink mt-1">Dimension scores</h3>
              </div>
              <TrendingUp className="h-5 w-5 text-brand-green" />
            </div>
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={barData} layout="vertical" margin={{ left: 0, right: 12 }}>
                  <CartesianGrid horizontal={false} stroke="#F1F5F9" />
                  <XAxis type="number" domain={[0, 5]} tick={{ fill: "#94A3B8", fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fill: "#64748B", fontSize: 11 }} width={130} />
                  <Tooltip cursor={{ fill: "rgba(15,76,58,0.05)" }} contentStyle={{ background: "#1A1C19", border: "none", borderRadius: 2, color: "white", fontSize: 12 }} />
                  <Bar dataKey="score" radius={[0, 4, 4, 0]}>
                    {barData.map((d, i) => (
                      <Cell key={i} fill={d.score >= 4 ? "#0F4C3A" : d.score >= 3 ? "#1A6B53" : d.score >= 2 ? "#C5A265" : "#B33A3A"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>

        {/* Strengths / Gaps */}
        <div className="mt-12 grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="bg-white border border-black/5 rounded-sm p-8">
            <div className="flex items-center gap-3 mb-5">
              <Award className="h-5 w-5 text-brand-green" />
              <h3 className="font-serif text-2xl text-brand-ink">Strengths to scale</h3>
            </div>
            <ul className="space-y-3">
              {insights.strengths.length === 0 && <li className="text-brand-slate text-sm">No standout strengths yet — focus on raising the floor.</li>}
              {insights.strengths.map(s => (
                <li key={s.name} className="flex items-start justify-between py-2 border-b border-black/5 last:border-0">
                  <span className="text-brand-ink">{s.name}</span>
                  <span className="font-serif text-brand-green font-medium">{s.score} / 5</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="bg-white border border-black/5 rounded-sm p-8">
            <div className="flex items-center gap-3 mb-5">
              <Target className="h-5 w-5 text-brand-gold" />
              <h3 className="font-serif text-2xl text-brand-ink">Priority gaps to close</h3>
            </div>
            <ul className="space-y-3">
              {insights.gaps.length === 0 && <li className="text-brand-slate text-sm">No critical gaps detected — maintain discipline.</li>}
              {insights.gaps.map(s => (
                <li key={s.name} className="flex items-start justify-between py-2 border-b border-black/5 last:border-0">
                  <span className="text-brand-ink">{s.name}</span>
                  <span className="font-serif text-[#B33A3A] font-medium">{s.score} / 5</span>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 bg-brand-ink text-white rounded-sm p-10 md:p-14 relative overflow-hidden">
          <div className="absolute -bottom-20 -right-20 w-80 h-80 rounded-full bg-brand-green/30 blur-3xl" />
          <div className="relative grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
            <div>
              <div className="text-xs font-bold tracking-[0.25em] uppercase text-brand-gold mb-3">Next Step</div>
              <h3 className="font-serif text-3xl md:text-4xl tracking-tight leading-tight">Turn this score into a roadmap.</h3>
              <p className="mt-4 text-white/70">Speak with a senior consultant for a confidential walkthrough of your results — and a 90-day action plan tailored to your context.</p>
            </div>
            <div className="flex flex-col gap-3 md:items-end">
              <Link to="/contact" data-testid="results-contact-cta" className="inline-flex items-center gap-2 bg-brand-gold text-brand-ink px-6 py-3.5 rounded-sm font-semibold hover:bg-white transition-colors">
                Book a Consultation <ArrowRight className="h-4 w-4" />
              </Link>
              <Link to="/growth-readiness" data-testid="results-growth-cta" className="inline-flex items-center gap-2 text-white/80 hover:text-brand-gold text-sm">
                View Growth Readiness Index <ArrowRight className="h-3 w-3" />
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AssessmentResults;